/**
 * WebSocket Transport for Tradovate Real-Time API
 *
 * Implements:
 * - Custom wire format protocol
 * - State machine (Closed/Connecting/Connected/Ready)
 * - Auto-reconnect with exponential backoff
 * - Heartbeat (send [] every 2.5s)
 * - Request/response correlation
 * - Event emission for market data, entity updates
 *
 * Ported from: websocket.rs (935 lines)
 */

import EventEmitter from 'eventemitter3';
import { ConnectionState, TransportError } from './types';
import type { WebSocketMessageData } from './types';
import { ConnectionManager } from './ConnectionManager';
import { RequestTracker } from './RequestTracker';
import { HeartbeatManager, HEARTBEAT_MESSAGE } from './HeartbeatManager';
import { WireFormat } from './WireFormat';

/**
 * WebSocket Transport
 *
 * Manages WebSocket connection to Tradovate with:
 * - Automatic reconnection
 * - Heartbeat keepalive
 * - Request/response correlation
 * - Event streaming
 *
 * Rust equivalent: WebSocketTransport (tokio + tungstenite / wasm + web-sys)
 */
export class WebSocketTransport extends EventEmitter {
  private ws: WebSocket | null = null;
  private getAuthToken: (() => string | null) | null = null;

  private connectionManager = new ConnectionManager();
  private requestTracker = new RequestTracker();
  private heartbeatManager = new HeartbeatManager();

  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts: number;
  private reconnectEnabled = true;
  private heartbeatCleanup: (() => void) | null = null;

  /**
   * @param url - Base URL (will append /websocket)
   * @param maxReconnectAttempts - Max reconnection attempts (default 5)
   */
  constructor(
    private url: string,
    maxReconnectAttempts: number = 5
  ) {
    super();
    this.maxReconnectAttempts = maxReconnectAttempts;
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.connectionManager.getState();
  }

  /**
   * Check if ready for requests
   */
  isReady(): boolean {
    return this.connectionManager.isReady();
  }

  /**
   * Enable or disable automatic reconnection
   */
  setReconnectEnabled(enabled: boolean): void {
    this.reconnectEnabled = enabled;
    if (!enabled) {
      console.log('🔌 WebSocket auto-reconnect disabled');
    }
  }

  /**
   * Get reconnection attempt count
   */
  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  /**
   * Connect to WebSocket
   *
   * Establishes WebSocket connection and waits for 'o' (open) frame.
   * Does NOT automatically authorize - call authorize() explicitly after this.
   *
   * Rust equivalent:
   * ```rust
   * pub async fn connect(&self) -> Result<(), ClientError> {
   *   self.connection_manager.set_state(ConnectionState::Connecting).await;
   *   let ws_stream = connect_async(url).await?;
   *   let (sink, read) = ws_stream.split();
   *   self.start_reader_loop(read).await;
   * }
   * ```
   */
  async connect(): Promise<void> {
    this.connectionManager.setState(ConnectionState.Connecting);

    // Build WebSocket URL
    let wsUrl = this.url
      .replace('http://', 'ws://')
      .replace('https://', 'wss://');

    if (!wsUrl.endsWith('/websocket')) {
      wsUrl = wsUrl.endsWith('/')
        ? `${wsUrl}websocket`
        : `${wsUrl}/websocket`;
    }

    console.log(`🔌 Connecting to WebSocket: ${wsUrl}`);

    return new Promise((resolve, reject) => {
      // Resolve when 'o' frame is received (Connected state)
      const onOpen = () => {
        this.off('ws:open', onOpen as any)
        resolve()
      }
      this.once('ws:open', onOpen as any)

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('✅ WebSocket connection established');
          this.connectionManager.setState(ConnectionState.Connected);
          // Will wait for 'o' frame before resolving
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          this.connectionManager.setState(ConnectionState.Closed);
          this.off('ws:open', onOpen as any)
          reject(new TransportError('WebSocket error', 'WS_ERROR'));
        };

        this.ws.onclose = (event) => {
          console.log(`🔌 WebSocket closed: ${event.code} ${event.reason}`);
          this.connectionManager.setState(ConnectionState.Closed);

          // Stop heartbeat
          if (this.heartbeatCleanup) {
            this.heartbeatCleanup();
            this.heartbeatCleanup = null;
          }

          // Cancel pending requests
          this.requestTracker.cancelAll('Connection closed');

          // Emit disconnect event
          this.emit('disconnect');

          // If we never received 'o' frame, reject the connect promise
          this.off('ws:open', onOpen as any)
          if (this.connectionManager.getState() === ConnectionState.Connecting) {
            reject(new TransportError('WebSocket closed before open frame', 'WS_CLOSED_BEFORE_OPEN', event.code))
          }

          // Attempt reconnect
          if (this.reconnectEnabled) {
            this.attemptReconnect();
          }
        };

      } catch (error) {
        this.connectionManager.setState(ConnectionState.Closed);
        this.off('ws:open', onOpen as any)
        reject(new TransportError(`Failed to create WebSocket: ${error}`, 'WS_CREATE_ERROR'));
      }
    });
  }

  /**
   * Authorize WebSocket connection
   *
   * Sends authorization message and waits for response.
   * Must be called AFTER connect() completes.
   * Format: authorize\n0\n\n{token}
   *
   * @param getAuthToken - Function that returns current auth token
   *
   * Rust equivalent:
   * ```rust
   * pub async fn authorize(&self, token: &str) -> Result<(), ClientError> {
   *   let auth_message = WireFormat::format_authorization(token);
   *   sink.send(Message::Text(auth_message)).await?;
   *   // Wait for response with request_id = 0
   *   connection_manager.set_state(ConnectionState::Ready).await;
   * }
   * ```
   */
  async authorize(getAuthToken: () => string | null): Promise<void> {
    if (this.connectionManager.getState() !== ConnectionState.Connected) {
      throw new TransportError(
        'Cannot authorize: WebSocket not connected',
        'NOT_CONNECTED'
      );
    }

    const authToken = getAuthToken();
    if (!authToken) {
      throw new TransportError('No auth token available', 'NO_AUTH_TOKEN');
    }

    const authMsg = WireFormat.formatAuthorization(authToken);
    console.log('🔐 Sending authorization...');

    this.sendRaw(authMsg);

    // Store for reconnect attempts
    this.getAuthToken = getAuthToken;

    // Wait for authorization response (request ID 0) before transitioning to Ready
    return new Promise((resolve, reject) => {
      const onAuthResponse = (dataMsg: WebSocketMessageData) => {
        if (dataMsg.type === 'response' && dataMsg.requestId === 0) {
          this.off('response', onAuthResponse);

          if (dataMsg.status === 200) {
            console.log('✅ Authorization successful');
            this.connectionManager.setState(ConnectionState.Ready);

            // Start heartbeat after successful auth
            this.startHeartbeat();

            this.emit('ready');
            resolve();
          } else {
            const error = new TransportError(
              `Authorization failed with status ${dataMsg.status}`,
              'AUTH_FAILED',
              dataMsg.status
            );
            reject(error);
            this.ws?.close();
          }
        }
      };

      this.on('response', onAuthResponse);

      // Set timeout for auth response
      const timeout = setTimeout(() => {
        this.off('response', onAuthResponse);
        reject(new TransportError('Authorization timeout', 'AUTH_TIMEOUT'));
      }, 10000); // 10 second timeout

      // Clear timeout on resolve/reject
      const originalResolve = resolve;
      const originalReject = reject;
      resolve = (...args) => {
        clearTimeout(timeout);
        originalResolve(...args);
      };
      reject = (...args) => {
        clearTimeout(timeout);
        originalReject(...args);
      };
    });
  }

  /**
   * Start heartbeat loop
   */
  private startHeartbeat(): void {
    this.heartbeatCleanup = this.heartbeatManager.startHeartbeatLoop(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendRaw(HEARTBEAT_MESSAGE);
        return true;  // Continue heartbeat
      }
      return false;  // Stop heartbeat
    });
  }

  /**
   * Handle incoming WebSocket message
   *
   * Rust equivalent: start_reader_loop()
   */
  private handleMessage(text: string): void {
    // Update heartbeat
    this.heartbeatManager.updateIncoming();

    // Parse wire format
    const message = WireFormat.parseFrame(text);
    if (!message) {
      console.warn('⚠️ Failed to parse WebSocket frame');
      return;
    }

    // Raw incoming WebSocket payload logging disabled (was useful for debugging).
    // if (message.type !== 'heartbeat') {
    //   console.log(`📥 WebSocket RAW: ${text.substring(0, 200)}${text.length > 200 ? '...' : ''}`);
    // }

    // Handle by message type
    switch (message.type) {
      case 'open':
        console.log('📨 Received "o" (open) frame');
        // Emit event that 'o' frame was received (connection ready for auth)
        this.emit('ws:open');
        break;

      case 'heartbeat':
        // Heartbeat received (logging disabled to reduce noise)
        break;

      case 'close':
        console.log('📨 Received "c" (close) frame');
        this.ws?.close();
        break;

      case 'data':
        // Process each message in the array
        for (const dataMsg of message.messages) {
          this.handleDataMessage(dataMsg);
        }
        break;
    }
  }

  /**
   * Handle parsed data message
   */
  private handleDataMessage(dataMsg: WebSocketMessageData): void {
    switch (dataMsg.type) {
      case 'response':
        // Complete pending request (response logging disabled to reduce noise)
        // console.log(`📬 Response for request ${dataMsg.requestId}: status ${dataMsg.status}`);

        if (dataMsg.status === 200) {
          this.requestTracker.completeRequest(dataMsg.requestId, dataMsg.data);
        } else {
          const error = new TransportError(
            `Request failed with status ${dataMsg.status}`,
            'REQUEST_FAILED',
            dataMsg.status
          );
          this.requestTracker.failRequest(dataMsg.requestId, error);
        }

        // Also emit for listeners (especially request_id=0 auth response)
        this.emit('response', dataMsg);
        break;

      case 'event':
      case 'md':
      case 'chart':
      case 'props':
      case 'sentiment':
        // Emit events for listeners
        this.emit('message', dataMsg);
        break;

      case 'disconnect':
        this.emit('disconnect');
        break;

      case 'unknown':
        console.warn('⚠️ Unknown message type:', dataMsg);
        this.emit('unknown', dataMsg);
        break;
    }
  }

  /**
   * Send raw message (internal)
   */
  private sendRaw(message: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new TransportError('WebSocket not connected', 'NOT_CONNECTED');
    }

    // Raw WebSocket payload logging disabled to reduce noise and avoid leaking endpoints/bodies in prod.
    // Uncomment locally for debugging if needed.
    // console.log(`📤 WebSocket SEND: ${message.substring(0, 200)}${message.length > 200 ? '...' : ''}`);
    this.ws.send(message);
    this.heartbeatManager.updateOutgoing();
  }

  /**
   * Close the WebSocket connection.
   * @param permanent If true, disables auto-reconnect before closing
   */
  public close(permanent: boolean = false): void {
    if (permanent) this.setReconnectEnabled(false)
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      this.ws.close()
    }
  }

  /**
   * Send request and wait for response
   *
   * @param endpoint - API endpoint (e.g., 'md/subscribeQuote')
   * @param body - Request body (optional)
   * @returns Response data
   *
   * @example
   * ```typescript
   * const response = await ws.request('user/syncrequest', { users: [userId] });
   * ```
   */
  async request(endpoint: string, body?: any): Promise<any> {
    if (!this.connectionManager.isReady()) {
      throw new TransportError(
        `WebSocket not ready (state: ${this.getState()})`,
        'NOT_READY'
      );
    }

    const requestId = this.requestTracker.nextId();
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
    const message = WireFormat.formatRequest(cleanEndpoint, requestId, body);

    // Track request (returns promise that resolves when response arrives)
    const responsePromise = this.requestTracker.trackRequest(requestId);

    // Send request
    this.sendRaw(message);

    // Wait for response
    return responsePromise;
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    console.log('🔌 Disconnecting WebSocket...');

    this.reconnectEnabled = false;  // Prevent auto-reconnect

    if (this.heartbeatCleanup) {
      this.heartbeatCleanup();
      this.heartbeatCleanup = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.connectionManager.setState(ConnectionState.Closed);
    this.requestTracker.cancelAll('Disconnected');

    console.log('✅ WebSocket disconnected');
  }

  /**
   * Attempt reconnection with exponential backoff
   *
   * Rust equivalent:
   * ```rust
   * async fn attempt_reconnect(&self) -> Result<(), ClientError> {
   *   let delay_seconds = 2u64.pow(current_attempt.saturating_sub(1));
   *   let delay_duration = Duration::from_secs(delay_seconds.min(30));
   *   tokio::time::sleep(delay_duration).await;
   *   self.connect().await
   * }
   * ```
   */
  private attemptReconnect(): void {
    if (!this.reconnectEnabled) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`❌ Max reconnect attempts (${this.maxReconnectAttempts}) reached`);
      this.emit('maxReconnectAttemptsReached');
      return;
    }

    this.reconnectAttempts++;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const delaySeconds = Math.min(
      Math.pow(2, this.reconnectAttempts - 1),
      30
    );

    console.log(
      `🔄 Reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} ` +
      `in ${delaySeconds}s...`
    );

    setTimeout(async () => {
      if (this.getAuthToken) {
        try {
          await this.connect();
          await this.authorize(this.getAuthToken);
          // Sync will be handled by the client after reconnect
        } catch (error) {
          console.error('❌ Reconnect failed:', error);
          // Will trigger another reconnect attempt via onclose
        }
      }
    }, delaySeconds * 1000);
  }

  /**
   * Reset reconnection attempts (call on successful connection)
   */
  // private resetReconnectAttempts(): void {
  //   this.reconnectAttempts = 0;
  // }

  /**
   * Subscribe to connection state changes
   *
   * @param listener - Callback function
   * @returns Unsubscribe function
   */
  onStateChange(listener: (state: ConnectionState) => void): () => void {
    return this.connectionManager.onStateChange(listener);
  }

  /**
   * Subscribe to messages
   *
   * @param listener - Callback for WebSocket messages
   * @returns Unsubscribe function
   *
   * @example
   * ```typescript
   * ws.onMessage((msg) => {
   *   if (msg.type === 'props') {
   *     entityStore.updateEntity(msg.repositoryEvent);
   *   }
   * });
   * ```
   */
  onMessage(listener: (message: WebSocketMessageData) => void): () => void {
    this.on('message', listener);
    return () => this.off('message', listener);
  }

  /**
   * Subscribe to disconnect events
   */
  onDisconnect(listener: () => void): () => void {
    this.on('disconnect', listener);
    return () => this.off('disconnect', listener);
  }

  /**
   * Subscribe to ready event
   */
  onReady(listener: () => void): () => void {
    this.on('ready', listener);
    return () => this.off('ready', listener);
  }

  /**
   * Transport interface implementation
   * Maps HTTP-style methods to WebSocket RPC requests
   */

  /**
   * GET request via WebSocket RPC
   * Query parameters are formatted as URL query string on line 3
   */
  async get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    if (!this.connectionManager.isReady()) {
      throw new TransportError(
        `WebSocket not ready (state: ${this.getState()})`,
        'NOT_READY'
      );
    }

    const requestId = this.requestTracker.nextId();
    const cleanEndpoint = path.startsWith('/') ? path.slice(1) : path;
    const message = WireFormat.formatRequest(cleanEndpoint, requestId, params, 'GET');

    console.log(`🔌 WS GET → [${cleanEndpoint}] params:`, params)
    console.log(`🔌 WS Wire format:\n${message.replace(/\n/g, '\\n\n')}`)

    const responsePromise = this.requestTracker.trackRequest(requestId);
    this.sendRaw(message);
    return responsePromise;
  }

  /**
   * POST request via WebSocket RPC
   * Body is formatted as JSON on line 4
   */
  async post<T = any>(path: string, body?: any): Promise<T> {
    if (!this.connectionManager.isReady()) {
      throw new TransportError(
        `WebSocket not ready (state: ${this.getState()})`,
        'NOT_READY'
      );
    }

    const requestId = this.requestTracker.nextId();
    const cleanEndpoint = path.startsWith('/') ? path.slice(1) : path;
    const message = WireFormat.formatRequest(cleanEndpoint, requestId, body, 'POST');

    const responsePromise = this.requestTracker.trackRequest(requestId);
    this.sendRaw(message);
    return responsePromise;
  }

  /**
   * PUT request via WebSocket RPC
   * Body is formatted as JSON on line 4
   */
  async put<T = any>(path: string, body?: any): Promise<T> {
    if (!this.connectionManager.isReady()) {
      throw new TransportError(
        `WebSocket not ready (state: ${this.getState()})`,
        'NOT_READY'
      );
    }

    const requestId = this.requestTracker.nextId();
    const cleanEndpoint = path.startsWith('/') ? path.slice(1) : path;
    const message = WireFormat.formatRequest(cleanEndpoint, requestId, body, 'PUT');

    const responsePromise = this.requestTracker.trackRequest(requestId);
    this.sendRaw(message);
    return responsePromise;
  }

  /**
   * DELETE request via WebSocket RPC
   */
  async delete<T = any>(path: string): Promise<T> {
    if (!this.connectionManager.isReady()) {
      throw new TransportError(
        `WebSocket not ready (state: ${this.getState()})`,
        'NOT_READY'
      );
    }

    const requestId = this.requestTracker.nextId();
    const cleanEndpoint = path.startsWith('/') ? path.slice(1) : path;
    const message = WireFormat.formatRequest(cleanEndpoint, requestId, undefined, 'DELETE');

    const responsePromise = this.requestTracker.trackRequest(requestId);
    this.sendRaw(message);
    return responsePromise;
  }
}
